interface FarePrice {
    rrp: number;
    rrpOriginal?: number;
    rrpWas?: number;
    defaultFare?: boolean;
    cprFare?: boolean;
}

/**
 * Returns the cheapest fare by `rrp`. Fares with a non-positive `rrp`
 * (e.g. $0 add-ons/infants) are ignored; if every fare is non-positive the
 * first fare is returned as a last resort.
 */
const cheapestFare = (faresPrices: FarePrice[]): FarePrice | null => {
    if (!faresPrices.length) return null;
    const priced = faresPrices.filter((fp) => typeof fp.rrp === "number" && fp.rrp > 0);
    const pool = priced.length ? priced : faresPrices;
    return pool.reduce((lowest, fp) => (fp.rrp < lowest.rrp ? fp : lowest), pool[0]);
};

/**
 * Returns the target fare for the "From" price display:
 * 1. If any fare has `defaultFare: true`, that fare is used.
 * 2. Otherwise the cheapest CPR fare (`cprFare: true`) is used. Creating a CPR
 *    fare often removes the product's default fare, so the CPR fares become the
 *    canonical "from" set.
 * 3. As a last resort (no default and no CPR fares), the cheapest fare overall
 *    is used.
 */
const getTargetFare = (faresPrices: FarePrice[]): FarePrice | null => {
    if (!faresPrices?.length) return null;

    const defaultFare = faresPrices.find((fp) => fp.defaultFare);
    if (defaultFare) return defaultFare;

    const cprFares = faresPrices.filter((fp) => fp.cprFare);
    if (cprFares.length) return cheapestFare(cprFares);

    return cheapestFare(faresPrices);
};

/**
 * Returns the from price string for display.
 * Uses the default fare, else the cheapest CPR fare, else the cheapest fare.
 * Shows the RRP — which is the discounted price if a discount applies.
 */
export const getDisplayPrice = (faresPrices: FarePrice[]): string => {
    const target = getTargetFare(faresPrices);
    if (!target) return "-";
    return `$${target.rrp?.toFixed(2)}`;
};

/**
 * Returns the from price and the original (was) price for display.
 * - `price`: the RRP on the target fare — default, else cheapest CPR, else cheapest (discounted if applicable)
 * - `originalPrice`: the rrpWas (slashed price) if the fare has been discounted, otherwise null
 */
export const getDisplayPriceWithOriginal = (
    faresPrices: FarePrice[],
): { price: string; originalPrice: string | null } => {
    const target = getTargetFare(faresPrices);
    if (!target) return { price: "-", originalPrice: null };

    const price = `$${target.rrp?.toFixed(2)}`;

    const originalPrice =
        target.rrpWas && target.rrp > 0 && target.rrp < target.rrpWas
            ? `$${target.rrpWas.toFixed(2)}`
            : null;

    return { price, originalPrice };
};

/**
 * Whether a number is safe to render as a price.
 *
 * Totals are derived from cart items, so before those land the arithmetic runs on
 * `undefined` and yields `NaN`, which `toFixed` happily renders as the string
 * "NaN". Callers check this and show a loading state instead: a total that is not
 * known yet should look like it is still arriving, never like a broken figure.
 */
export const isDisplayablePrice = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

/** `$0.00`-style price, or null when the value is not known yet. */
export const formatPrice = (value: unknown): string | null =>
    isDisplayablePrice(value) ? `$${value.toFixed(2)}` : null;
